/**
 * Plugin Amplitude pour Vue 2
 * Wrapper autour de l'instance globale window.amplitude
 */

const AmplitudePlugin = {
  install(Vue) {
    // Helper pour vérifier si Amplitude est disponible
    const isAmplitudeReady = () => {
      return typeof window !== 'undefined' && 
             window.amplitude && 
             typeof window.amplitude.track === 'function';
    };

    // Méthodes de tracking
    Vue.prototype.$amplitude = {
      /**
       * Track un événement custom
       * @param {string} eventName - Nom de l'événement
       * @param {object} properties - Propriétés de l'événement
       */
      track(eventName, properties = {}) {
        if (!isAmplitudeReady()) {
          console.warn('[Amplitude] Not initialized, skipping track:', eventName);
          return;
        }
        
        try {
          window.amplitude.track(eventName, properties);
          console.log('[Amplitude] Event tracked:', eventName, properties);
        } catch (err) {
          console.error('[Amplitude] Track error:', err.message);
        }
      },

      /**
       * Identifie l'utilisateur
       * @param {string} userId - ID de l'utilisateur
       * @param {object} userProperties - Propriétés de l'utilisateur
       */
      identify(userId, userProperties = {}) {
        if (!isAmplitudeReady()) {
          console.warn('[Amplitude] Not initialized, skipping identify');
          return;
        }

        try {
          if (userId) {
            window.amplitude.setUserId(userId);
          }
          
          if (Object.keys(userProperties).length > 0) {
            const identifyEvent = new window.amplitude.Identify();
            Object.entries(userProperties).forEach(([key, value]) => {
              identifyEvent.set(key, value);
            });
            window.amplitude.identify(identifyEvent);
          }
          
          console.log('[Amplitude] User identified:', userId, userProperties);
        } catch (err) {
          console.error('[Amplitude] Identify error:', err.message);
        }
      },

      /**
       * Set user properties
       * @param {object} properties - Propriétés à set
       */
      setUserProperties(properties = {}) {
        if (!isAmplitudeReady()) {
          console.warn('[Amplitude] Not initialized, skipping setUserProperties');
          return;
        }

        try {
          const identifyEvent = new window.amplitude.Identify();
          Object.entries(properties).forEach(([key, value]) => {
            identifyEvent.set(key, value);
          });
          window.amplitude.identify(identifyEvent);
          console.log('[Amplitude] User properties set:', properties);
        } catch (err) {
          console.error('[Amplitude] SetUserProperties error:', err.message);
        }
      },

      /**
       * Track revenue (pour les conversions)
       * @param {number} amount - Montant
       * @param {object} properties - Propriétés additionnelles
       */
      trackRevenue(amount, properties = {}) {
        if (!isAmplitudeReady()) {
          console.warn('[Amplitude] Not initialized, skipping trackRevenue');
          return;
        }

        try {
          const revenue = new window.amplitude.Revenue()
            .setPrice(amount)
            .setQuantity(1);

          if (properties.productId) {
            revenue.setProductId(properties.productId);
          }
          
          window.amplitude.revenue(revenue);
          
          // Track aussi comme événement custom
          this.track('Purchase', {
            amount,
            ...properties
          });
          
          console.log('[Amplitude] Revenue tracked:', amount, properties);
        } catch (err) {
          console.error('[Amplitude] TrackRevenue error:', err.message);
        }
      },

      /**
       * Reset l'utilisateur (logout)
       */
      reset() {
        if (!isAmplitudeReady()) {
          console.warn('[Amplitude] Not initialized, skipping reset');
          return;
        }

        try {
          window.amplitude.reset();
          console.log('[Amplitude] User reset');
        } catch (err) {
          console.error('[Amplitude] Reset error:', err.message);
        }
      }
    };

    // Helper global pour les événements de conversion
    Vue.prototype.$trackConversion = {
      /**
       * Track une tentative d'upgrade
       */
      upgradeAttempt(reason, planTarget = 'pro') {
        Vue.prototype.$amplitude.track('Upgrade_Attempt', {
          reason,
          plan_target: planTarget,
          timestamp: new Date().toISOString()
        });
      },

      /**
       * Track un hit de limite
       */
      limitHit(limitType, currentValue, maxValue) {
        Vue.prototype.$amplitude.track('Limit_Hit', {
          limit_type: limitType,
          current_value: currentValue,
          max_value: maxValue,
          timestamp: new Date().toISOString()
        });
      },

      /**
       * Track une feature bloquée
       */
      featureBlocked(featureName, requiredPlan = 'pro') {
        Vue.prototype.$amplitude.track('Feature_Blocked', {
          feature_name: featureName,
          required_plan: requiredPlan,
          timestamp: new Date().toISOString()
        });
      },

      /**
       * Track un plan viewed
       */
      planViewed(source = 'direct') {
        Vue.prototype.$amplitude.track('Plan_Viewed', {
          source,
          timestamp: new Date().toISOString()
        });
      },

      /**
       * Track un checkout started
       */
      checkoutStarted(plan, price) {
        Vue.prototype.$amplitude.track('Checkout_Started', {
          plan,
          price,
          timestamp: new Date().toISOString()
        });
      },

      /**
       * Track un purchase completed
       */
      purchaseCompleted(plan, price, transactionId) {
        Vue.prototype.$amplitude.track('Purchase_Completed', {
          plan,
          price,
          transaction_id: transactionId,
          timestamp: new Date().toISOString()
        });
        
        // Track revenue
        Vue.prototype.$amplitude.trackRevenue(price, {
          productId: plan,
          transactionId
        });
      }
    };
  }
};

export default AmplitudePlugin;









